/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 5:13:55AM]
Permissions to update outside the main branch: [true]
*/

import {
    DesignType,
    DropDown,
    SettingTable,
    useErrorState,
    useLocalStorage,
    WorkplaceSetting
} from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getCultures, getDesign } from 'axios/dictionaryApi';
import { getAppSettings } from 'axios/settingsApi';
import { getWorkplaceSettingsOnTechnicalWorks } from 'axios/workplaceSettingsApi';
import {
    dateFormat as systemdateFormat,
    timeFormat as systemtimeFormat
} from 'constans/dateFormat';
import { setDefaultOptions as setDateDefaultOptions } from 'date-fns';
import useApi from 'hooks/useApi';
import { entries } from 'lodash';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import DictItem from 'models/api/dictItem';
import { GeneralSettings } from 'models/generalSettings';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { registerLocale, setDefaultLocale as setDatePickerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import axios from '../axios/axios.paylater-api';
import { changeConfigItem, getConfigItem } from '../axios/configApi';
import useAuth from '../hooks/useAuth';
import { ConfigItemChange } from '../models/api/config';
import locales from '../models/locales';

const GeneralSettingsContext = React.createContext<GeneralSettings>({} as GeneralSettings);

const defaultDatetimeFormat = systemdateFormat + ' ' + systemtimeFormat;

type GeneralSettingsProviderProps = React.PropsWithChildren<{
    fallback?: ReactNode;
}>;

export function getSetting(webSettings: SettingTable[], settingCode: string, cultureCode: string) {
    const format = webSettings.find((item) => item.code === settingCode);
    if (format == null) {
        return;
    }
    const formatValue = format.translations.find((item) => item.cultureCode === cultureCode);
    if (formatValue == null) {
        return;
    }
    return formatValue.value;
}

const GeneralSettingsProvider = ({ fallback, children }: GeneralSettingsProviderProps) => {
    const api = useApi();
    const { hasRole } = useAuth();
    const { t, i18n } = useTranslation();
    const { handleRequestError } = useErrorState();

    const [webSettings, setWebSetting] = useState<SettingTable[]>([]);
    const [cultures, setCultures] = useState<DictItem[]>([]);
    const [workplaceSettings, setWorkplaceSettings] = useState<WorkplaceSetting[]>([]);

    const [instanceName, setInstanceName] = useState<string>();
    const [dateFormat, setDateFormat] = useState<string>(systemdateFormat);
    const [timeFormat, setTimeFormat] = useState<string>(systemtimeFormat);
    const [dateTimeFormat, setDateTimeFormat] = useState<string>(defaultDatetimeFormat);
    const [designTypes, setDesignTypes] = useState<DropDown<DesignType>[]>();

    const [appVersion, setAppVersion] = useState<string>();

    const [instanceRoot, setInstanceRoot] = useState<string>();

    const [designType, setDesignType] = useLocalStorage<DesignType>(
        'general/design-type',
        'classic'
    );

    const [operationDay, setOperationDay] = useState<boolean>(false);
    const [operationalDate, setOperationalDate] = useState<Date>(new Date());
    const [operationalNextDate, setOperationalNextDate] = useState<Date>(new Date());

    useEffect(() => {
        //Регистрация локалей для datePiker
        entries(locales).map(([key, value]) => registerLocale(key, value));

        setDatePickerLocale('en');
    }, []);

    useEffect(() => {
        setDatePickerLocale(i18n.language);
        setDateDefaultOptions({
            locale: locales[i18n.language],
        });
    }, [i18n.language]);

    useQuery('settings', () => getAppSettings(api), {
        onSuccess: (data) => {
            setWebSetting(data);
        },
        onError: (e: AxiosError<AxiosErrorResponse>) => handleRequestError(e.response?.data),
        enabled: !webSettings.length,
    });

    useQuery('culture', () => getCultures(axios), {
        onSuccess: (data) => {
            setCultures(data);
        },
        onError: (e: AxiosError<AxiosErrorResponse>) => handleRequestError(e.response?.data),
        enabled: !cultures.length,
    });

    useQuery('design', () => getDesign(axios), {
        onSuccess: (data) => {
            if (data.length === 1) setDesignType(data[0].id ?? undefined);
            setDesignTypes(data);
        },
        onError: (e: AxiosError<AxiosErrorResponse>) => handleRequestError(e.response?.data),
        enabled: !cultures.length,
    });

    const { refetch: refetchWorkplaceSettings } = useQuery(
        'workplaceSettingsOnTechnicalWorks',
        () => getWorkplaceSettingsOnTechnicalWorks(axios),
        {
            onSuccess: (data) => {
                setWorkplaceSettings(data);
            },
            onError: (e: AxiosError<AxiosErrorResponse>) => handleRequestError(e.response?.data),
            enabled: !workplaceSettings.length,
        }
    );

    const { refetch: refetchOperationDay } = useQuery(
        'operationDay',
        () => getConfigItem(axios, 'OperationalDayOpen'),
        {
            onSuccess: (data) => {
                const od = data && data.value === '1';
                setOperationDay(od);
            },
            onError: (e: AxiosError<AxiosErrorResponse>) => handleRequestError(e.response?.data),
            enabled: hasRole('configGetById'),
        }
    );

    const { refetch: refetchOperationalDate } = useQuery('operationalDate', () => getConfigItem(axios, 'OperationalDate'), {
        onSuccess: (data) => {
            if (data?.value) {
                const date = new Date(data.value);
                setOperationalDate(date);
            }
        },
        onError: (e: AxiosError<AxiosErrorResponse>) => handleRequestError(e.response?.data),
        enabled: hasRole('configGetById'),
    });

    const { refetch: refetchOperationalNextDate } = useQuery('operationalNextDate', () => getConfigItem(axios, 'OperationalNextDate'), {
        onSuccess: (data) => {
            if (data?.value) {
                const date = new Date(data.value);
                setOperationalNextDate(date);
            }
        },
        onError: (e: AxiosError<AxiosErrorResponse>) => handleRequestError(e.response?.data),
        enabled: hasRole('configGetById'),
    });

    useEffect(() => {
        document.documentElement.setAttribute('design-type', designType ?? 'classic');
    }, [designType]);

    useEffect(() => {
        setInstanceName(getSetting(webSettings, 'InstanceName', i18n.language));
        setDateFormat(getSetting(webSettings, 'DateFormat', i18n.language) || systemdateFormat);
        setTimeFormat(getSetting(webSettings, 'TimeFormat', i18n.language) || systemtimeFormat);
        setDateTimeFormat(
            getSetting(webSettings, 'DateTimeFormat', i18n.language) || defaultDatetimeFormat
        );
        setInstanceRoot(getSetting(webSettings, 'InstanceRoot', i18n.language));
        setAppVersion(getSetting(webSettings, 'AppVersion', i18n.language));
    }, [webSettings, t]);

    const generalTechnicalWork = useMemo(() => {
        return !!workplaceSettings.find((x) => x.path === '/')?.onTechnicalWork;
    }, [workplaceSettings]);

    const updateConfigMutation = useMutation({
        mutationFn: (configItem: ConfigItemChange) => changeConfigItem(api, configItem),
        onSuccess: () => {
            refetchOperationDay();
            refetchOperationalDate();
            refetchOperationalNextDate();
        },
    });

    const contextValue = useMemo<GeneralSettings>(
        () => ({
            cultures,
            settings: webSettings,
            instanceName,
            dateFormat,
            timeFormat,
            dateTimeFormat,
            appVersion,
            generalTechnicalWork,
            workplaceSettings,
            refetchWorkplaceSettings,
            instanceRoot,
            designType: designType ?? 'classic',
            designTypes,
            setDesignType,
            operationDay,
            updateConfigMutation,
            operationalDate,
            operationalNextDate,
        }),
        [
            cultures,
            webSettings,
            instanceName,
            dateFormat,
            timeFormat,
            dateTimeFormat,
            appVersion,
            generalTechnicalWork,
            workplaceSettings,
            refetchWorkplaceSettings,
            instanceRoot,
            designType,
            designTypes,
            setDesignType,
            operationDay,
            operationalDate,
            operationalNextDate,
        ]
    );

    return (
        <GeneralSettingsContext.Provider value={contextValue}>
            {!webSettings.length || !cultures.length ? fallback : children}
        </GeneralSettingsContext.Provider>
    );
};

const useGeneralSettingsContext = () => {
    const context = React.useContext(GeneralSettingsContext);
    return context;
};

export { GeneralSettingsProvider, useGeneralSettingsContext };
export default GeneralSettingsProvider;
